(() => {
  'use strict';

  const REGISTRY_KEY = '__BUContentCardSliderRegistry';
  const VERSION = '2026-09-14-mobile-touch-v6';

  const previousRegistry =
    window[REGISTRY_KEY];

  if (
    previousRegistry &&
    typeof previousRegistry.destroyAll ===
      'function'
  ) {
    previousRegistry.destroyAll();
  }

  const ROOT_SELECTOR = '[data-bu-content-slider]';
  const VIEWPORT_SELECTOR = '[data-bu-slider-viewport]';
  const TRACK_SELECTOR = '[data-bu-slider-track]';
  const SLIDE_SELECTOR = '[data-bu-slider-slide]';
  const PREV_SELECTOR = '[data-bu-slider-prev]';
  const NEXT_SELECTOR = '[data-bu-slider-next]';
  const STATUS_SELECTOR = '[data-bu-slider-status]';

  const MOBILE_QUERY = '(max-width: 749px)';
  const REDUCED_MOTION_QUERY =
    '(prefers-reduced-motion: reduce)';

  const instances = new Map();

  const raf =
    window.requestAnimationFrame ||
    ((callback) => window.setTimeout(callback, 16));

  const clamp = (value, min, max) =>
    Math.max(min, Math.min(max, value));

  const now = () =>
    window.performance && performance.now
      ? performance.now()
      : Date.now();

  const parseBoolean = (value) =>
    String(value) === 'true';

  const parseNumber = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  };

  class BUContentCardSlider {
    constructor(root) {
      this.root = root;

      this.section = root.closest(
        '.bu-content-card-slider-section'
      );

      this.viewport = root.querySelector(
        VIEWPORT_SELECTOR
      );

      this.track = root.querySelector(
        TRACK_SELECTOR
      );

      this.slides = Array.from(
        root.querySelectorAll(SLIDE_SELECTOR)
      );

      this.prevButton = this.section
        ? this.section.querySelector(PREV_SELECTOR)
        : null;

      this.nextButton = this.section
        ? this.section.querySelector(NEXT_SELECTOR)
        : null;

      this.status = root.querySelector(
        STATUS_SELECTOR
      );

      this.interactionArea =
        this.section || this.root;

      this.index = 0;
      this.maxIndex = 0;

      this.slideWidth = 0;
      this.gap = 0;
      this.step = 0;

      this.viewportWidth = 0;
      this.trackContentWidth = 0;

      this.currentTranslate = 0;
      this.minTranslate = 0;
      this.maxTranslate = 0;

      this.mobile = false;
      this.destroyed = false;

      this.clickGuardUntil = 0;
      this.resizeFrame = null;

      this.autoplayTimer = null;
      this.autoplayResumeTimer = null;

      this.pauseReasons = new Set();
      this.listeners = [];

      this.drag = {
        active: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        startTranslate: 0,
        deltaX: 0,
        absX: 0,
        horizontalLocked: false,
        captured: false,
        pressedLink: null,
        startedAt: 0
      };

      this.mobileMedia =
        window.matchMedia(MOBILE_QUERY);

      this.reducedMotionMedia =
        window.matchMedia(
          REDUCED_MOTION_QUERY
        );

      this.onPointerDown =
        this.onPointerDown.bind(this);

      this.onPointerMove =
        this.onPointerMove.bind(this);

      this.onPointerEnd =
        this.onPointerEnd.bind(this);

      this.onPointerCancel =
        this.onPointerCancel.bind(this);

      this.onLostPointerCapture =
        this.onLostPointerCapture.bind(this);

      this.onClickCapture =
        this.onClickCapture.bind(this);

      this.onPreviousClick =
        this.onPreviousClick.bind(this);

      this.onNextClick =
        this.onNextClick.bind(this);

      this.onKeyDown =
        this.onKeyDown.bind(this);

      this.onResize =
        this.onResize.bind(this);

      this.onVisibilityChange =
        this.onVisibilityChange.bind(this);

      this.onMouseEnter =
        this.onMouseEnter.bind(this);

      this.onMouseLeave =
        this.onMouseLeave.bind(this);

      this.onFocusIn =
        this.onFocusIn.bind(this);

      this.onFocusOut =
        this.onFocusOut.bind(this);

      this.onReducedMotionChange =
        this.onReducedMotionChange.bind(this);

      this.preventNativeDrag =
        this.preventNativeDrag.bind(this);

      this.init();
    }

    readSettings() {
      const data = this.root.dataset;

      this.settings = {
        cardsDesktop: Math.max(
          1,
          Math.round(
            parseNumber(
              data.cardsDesktop,
              3
            )
          )
        ),

        /*
          모바일 오른쪽 미리보기 비율은 카드 폭을 만드는
          CSS 변수에 사용됩니다.

          JS에서는 왼쪽 미리보기용 translate 값을
          절대 추가하지 않습니다.
        */
        mobileRightPeek: clamp(
          parseNumber(
            data.mobileRightPeek,
            15
          ),
          0,
          30
        ),

        transitionDuration: clamp(
          parseNumber(
            data.transitionDuration,
            440
          ),
          0,
          3000
        ),

        dragThreshold: clamp(
          parseNumber(
            data.dragThreshold,
            28
          ),
          5,
          200
        ),

        keyboard: parseBoolean(
          data.keyboard
        ),

        autoplay: parseBoolean(
          data.autoplay
        ),

        autoplayInterval: Math.max(
          1000,
          parseNumber(
            data.autoplayInterval,
            4000
          )
        ),

        pauseHover: parseBoolean(
          data.pauseHover
        ),

        pauseFocus: parseBoolean(
          data.pauseFocus
        ),

        resumeDelay: Math.max(
          0,
          parseNumber(
            data.resumeDelay,
            5000
          )
        )
      };
    }

    init() {
      if (
        !this.root ||
        !this.viewport ||
        !this.track ||
        this.slides.length === 0
      ) {
        return;
      }

      this.readSettings();
      this.measure(false);

      this.root.classList.add('is-ready');

      this.bindEvents();
      this.updateControls();

      if (
        this.reducedMotionMedia.matches
      ) {
        this.pauseReasons.add(
          'reduced-motion'
        );
      }

      this.scheduleAutoplay();
    }

    addListener(
      target,
      type,
      handler,
      options
    ) {
      if (!target) return;

      target.addEventListener(
        type,
        handler,
        options
      );

      this.listeners.push({
        target,
        type,
        handler,
        options
      });
    }

    bindEvents() {
      this.addListener(
        this.viewport,
        'pointerdown',
        this.onPointerDown,
        { passive: true }
      );

      this.addListener(
        this.viewport,
        'pointermove',
        this.onPointerMove,
        { passive: false }
      );

      this.addListener(
        this.viewport,
        'pointerup',
        this.onPointerEnd,
        { passive: true }
      );

      this.addListener(
        this.viewport,
        'pointercancel',
        this.onPointerCancel,
        { passive: true }
      );

      this.addListener(
        this.viewport,
        'lostpointercapture',
        this.onLostPointerCapture,
        { passive: true }
      );

      this.addListener(
        this.viewport,
        'click',
        this.onClickCapture,
        true
      );

      this.addListener(
        this.root,
        'dragstart',
        this.preventNativeDrag,
        false
      );

      this.addListener(
        this.prevButton,
        'click',
        this.onPreviousClick,
        false
      );

      this.addListener(
        this.nextButton,
        'click',
        this.onNextClick,
        false
      );

      if (this.settings.keyboard) {
        this.addListener(
          this.viewport,
          'keydown',
          this.onKeyDown,
          false
        );
      }

      if (this.settings.pauseHover) {
        this.addListener(
          this.interactionArea,
          'mouseenter',
          this.onMouseEnter,
          false
        );

        this.addListener(
          this.interactionArea,
          'mouseleave',
          this.onMouseLeave,
          false
        );
      }

      if (this.settings.pauseFocus) {
        this.addListener(
          this.interactionArea,
          'focusin',
          this.onFocusIn,
          false
        );

        this.addListener(
          this.interactionArea,
          'focusout',
          this.onFocusOut,
          false
        );
      }

      this.addListener(
        document,
        'visibilitychange',
        this.onVisibilityChange,
        false
      );

      this.addListener(
        window,
        'resize',
        this.onResize,
        { passive: true }
      );

      if (
        typeof this.reducedMotionMedia
          .addEventListener === 'function'
      ) {
        this.reducedMotionMedia.addEventListener(
          'change',
          this.onReducedMotionChange
        );
      } else if (
        typeof this.reducedMotionMedia
          .addListener === 'function'
      ) {
        this.reducedMotionMedia.addListener(
          this.onReducedMotionChange
        );
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver =
          new ResizeObserver(
            this.onResize
          );

        this.resizeObserver.observe(
          this.viewport
        );
      }
    }

    preventNativeDrag(event) {
      event.preventDefault();
    }

    measure(animate = false) {
      if (
        this.destroyed ||
        !this.viewport ||
        !this.track ||
        this.slides.length === 0
      ) {
        return;
      }

      this.readSettings();

      this.mobile =
        this.mobileMedia.matches;

      this.viewportWidth = Math.max(
        1,
        this.viewport
          .getBoundingClientRect()
          .width
      );

      const trackStyle =
        window.getComputedStyle(
          this.track
        );

      this.gap = parseNumber(
        trackStyle.columnGap ||
          trackStyle.gap,
        0
      );

      this.slideWidth =
        this.slides[0]
          .getBoundingClientRect()
          .width ||
        this.viewportWidth;

      this.step =
        this.slideWidth + this.gap;

      const visibleCards = this.mobile
        ? 1
        : this.settings.cardsDesktop;

      this.maxIndex = Math.max(
        0,
        this.slides.length -
          visibleCards
      );

      this.index = clamp(
        this.index,
        0,
        this.maxIndex
      );

      /*
        실제 카드와 gap을 합친 트랙의 총 콘텐츠 너비입니다.

        모바일 마지막 카드에서는 이 값을 기준으로
        마지막 카드의 오른쪽 끝을 viewport 오른쪽 끝에
        정확하게 맞춥니다.

        따라서 마지막 카드 뒤에는 모바일 미리보기 비율만큼의
        추가 빈 공간이 절대 남지 않습니다.
      */
      this.trackContentWidth =
        this.slideWidth *
          this.slides.length +
        this.gap *
          Math.max(
            0,
            this.slides.length - 1
          );

      this.maxTranslate = 0;

      if (this.mobile) {
        this.minTranslate = Math.min(
          0,
          this.viewportWidth -
            this.trackContentWidth
        );
      } else {
        this.minTranslate =
          0 -
          this.maxIndex *
            this.step;
      }

      this.root.classList.toggle(
        'is-static',
        this.maxIndex === 0
      );

      this.goTo(this.index, {
        animate,
        announce: false,
        userInitiated: false
      });
    }

    /*
      모바일:
      - 첫 카드는 translateX(0)으로 좌측 정렬
      - 중간 카드는 한 장씩 정상 이동
      - 마지막 카드는 실제 트랙 끝에 맞춰 추가 빈 공간 제거

      데스크탑:
      - 기존 한 장 단위 이동 유지
    */
    calculateTranslate(index) {
      if (this.maxIndex === 0) {
        return 0;
      }

      if (!this.mobile) {
        return 0 - index * this.step;
      }

      if (index <= 0) {
        return 0;
      }

      if (index >= this.maxIndex) {
        return this.minTranslate;
      }

      return Math.max(
        this.minTranslate,
        0 - index * this.step
      );
    }

    setTranslate(
      value,
      animate = true
    ) {
      if (!animate) {
        this.track.classList.add(
          'is-no-transition'
        );
      } else {
        this.track.classList.remove(
          'is-no-transition'
        );
      }

      this.track.style.transform =
        `translate3d(${value}px, 0, 0)`;

      this.currentTranslate = value;

      if (!animate) {
        raf(() => {
          if (
            !this.drag.active &&
            this.track
          ) {
            this.track.classList.remove(
              'is-no-transition'
            );
          }
        });
      }
    }

    goTo(
      targetIndex,
      options = {}
    ) {
      const {
        animate = true,
        announce = true,
        userInitiated = false
      } = options;

      const nextIndex = clamp(
        Math.round(targetIndex),
        0,
        this.maxIndex
      );

      this.index = nextIndex;

      this.setTranslate(
        this.calculateTranslate(
          nextIndex
        ),
        animate &&
          !this.reducedMotionMedia
            .matches
      );

      this.updateControls();

      if (announce) {
        this.announcePosition();
      }

      if (userInitiated) {
        this.handleUserInteraction();
      }
    }

    goBy(delta, options = {}) {
      this.goTo(
        this.index + delta,
        options
      );
    }

    updateControls() {
      const atStart =
        this.index <= 0;

      const atEnd =
        this.index >= this.maxIndex;

      const isStatic =
        this.maxIndex === 0;

      if (this.prevButton) {
        const disabled =
          isStatic || atStart;

        this.prevButton.disabled =
          disabled;

        this.prevButton.setAttribute(
          'aria-disabled',
          String(disabled)
        );
      }

      if (this.nextButton) {
        const disabled =
          isStatic || atEnd;

        this.nextButton.disabled =
          disabled;

        this.nextButton.setAttribute(
          'aria-disabled',
          String(disabled)
        );
      }

      const visibleCards = this.mobile
        ? 1
        : this.settings.cardsDesktop;

      const visibleEnd = Math.min(
        this.slides.length - 1,
        this.index +
          visibleCards -
          1
      );

      this.slides.forEach(
        (slide, slideIndex) => {
          const isLeadingSlide =
            slideIndex === this.index;

          const isVisible =
            slideIndex >= this.index &&
            slideIndex <= visibleEnd;

          const cardLink =
            slide.querySelector(
              '.bu-content-card-slider__card-link'
            );

          if (isLeadingSlide) {
            slide.setAttribute(
              'data-current',
              'true'
            );
          } else {
            slide.removeAttribute(
              'data-current'
            );
          }

          slide.setAttribute(
            'aria-hidden',
            String(!isVisible)
          );

          if (cardLink) {
            if (isVisible) {
              cardLink.removeAttribute(
                'tabindex'
              );
            } else {
              cardLink.setAttribute(
                'tabindex',
                '-1'
              );
            }
          }
        }
      );
    }

    announcePosition() {
      if (!this.status) return;

      this.status.textContent =
        `${this.index + 1} / ${this.slides.length}`;
    }

    onPreviousClick() {
      if (this.index <= 0) return;

      this.goBy(-1, {
        animate: true,
        announce: true,
        userInitiated: true
      });
    }

    onNextClick() {
      if (
        this.index >=
        this.maxIndex
      ) {
        return;
      }

      this.goBy(1, {
        animate: true,
        announce: true,
        userInitiated: true
      });
    }

    onKeyDown(event) {
      if (
        event.key === 'ArrowLeft'
      ) {
        event.preventDefault();
        this.onPreviousClick();
        return;
      }

      if (
        event.key === 'ArrowRight'
      ) {
        event.preventDefault();
        this.onNextClick();
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();

        this.goTo(0, {
          animate: true,
          announce: true,
          userInitiated: true
        });

        return;
      }

      if (event.key === 'End') {
        event.preventDefault();

        this.goTo(
          this.maxIndex,
          {
            animate: true,
            announce: true,
            userInitiated: true
          }
        );
      }
    }

    onPointerDown(event) {
      if (
        event.pointerType ===
          'mouse' &&
        event.button !== 0
      ) {
        return;
      }

      /*
        멀티터치의 보조 포인터나 이미 진행 중인 포인터는
        새 드래그로 시작하지 않습니다.
      */
      if (
        event.isPrimary === false ||
        this.drag.active
      ) {
        return;
      }

      const pressedSlide =
        event.target &&
        typeof event.target.closest === 'function'
          ? event.target.closest(
              SLIDE_SELECTOR
            )
          : null;

      const pressedLink = pressedSlide
        ? pressedSlide.querySelector(
            '.bu-content-card-slider__card-link[href]'
          )
        : null;

      /*
        슬라이드할 카드가 없고 링크도 없는 경우에는
        포인터 상태를 만들 필요가 없습니다.
      */
      if (
        this.maxIndex === 0 &&
        !pressedLink
      ) {
        return;
      }

      this.clearAutoplayTimers();
      this.pauseReasons.add('drag');

      this.drag.active = true;
      this.drag.pointerId =
        event.pointerId;

      this.drag.startX =
        event.clientX;

      this.drag.startY =
        event.clientY;

      this.drag.startTranslate =
        this.currentTranslate;

      this.drag.deltaX = 0;
      this.drag.absX = 0;
      this.drag.horizontalLocked =
        false;
      this.drag.captured = false;
      this.drag.pressedLink =
        pressedLink;
      this.drag.startedAt = now();

      /*
        iOS Safari에서는 손가락이 움직인 뒤 capture를 시도하면
        브라우저의 스크롤 제스처 판정 과정에서 pointercancel이
        먼저 발생할 수 있습니다.

        따라서 슬라이드가 가능한 경우 pointerdown 즉시 viewport가
        포인터를 capture합니다. 다만 이 시점에는 preventDefault()를
        하지 않으므로 세로 스크롤은 그대로 유지됩니다.
      */
      if (this.maxIndex > 0) {
        try {
          this.viewport.setPointerCapture(
            event.pointerId
          );
          this.drag.captured = true;
        } catch (_) {
          this.drag.captured = false;
        }
      }
    }

    onPointerMove(event) {
      if (
        !this.drag.active ||
        event.pointerId !==
          this.drag.pointerId
      ) {
        return;
      }

      const deltaX =
        event.clientX -
        this.drag.startX;

      const deltaY =
        event.clientY -
        this.drag.startY;

      const absoluteX =
        Math.abs(deltaX);

      const absoluteY =
        Math.abs(deltaY);

      this.drag.deltaX = deltaX;
      this.drag.absX = absoluteX;

      if (
        !this.drag.horizontalLocked
      ) {
        /*
          약 6px 전까지는 클릭/탭으로 취급하고 아무 것도 막지 않습니다.
          그 이후 최초 방향을 판정해 세로가 우세하면 즉시 포인터를
          정리하여 페이지 스크롤을 유지하고, 가로가 우세할 때만
          실제 슬라이드 드래그를 시작합니다.
        */
        if (
          Math.max(
            absoluteX,
            absoluteY
          ) <= 6
        ) {
          return;
        }

        if (absoluteY > absoluteX) {
          this.cancelDragForVerticalScroll(
            event.pointerId
          );

          return;
        }

        if (
          this.maxIndex > 0
        ) {
          this.drag.horizontalLocked =
            true;

          this.root.classList.add(
            'is-dragging'
          );

          this.track.classList.add(
            'is-dragging'
          );
        }
      }

      if (
        !this.drag.horizontalLocked
      ) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      let nextTranslate =
        this.drag.startTranslate +
        deltaX;

      /*
        첫 카드보다 오른쪽 또는 마지막 카드의 실제 끝보다
        왼쪽으로 드래그하면 저항을 적용합니다.

        minTranslate가 실제 트랙 끝이므로 모바일 마지막 카드 뒤에
        불필요한 빈 공간이 생기지 않습니다.
      */
      if (
        nextTranslate >
        this.maxTranslate
      ) {
        nextTranslate =
          this.maxTranslate +
          (
            nextTranslate -
            this.maxTranslate
          ) *
            0.28;
      } else if (
        nextTranslate <
        this.minTranslate
      ) {
        nextTranslate =
          this.minTranslate +
          (
            nextTranslate -
            this.minTranslate
          ) *
            0.28;
      }

      this.setTranslate(
        nextTranslate,
        false
      );
    }

    cancelDragForVerticalScroll(
      pointerId
    ) {
      this.drag.active = false;

      this.root.classList.remove(
        'is-dragging'
      );

      this.track.classList.remove(
        'is-dragging'
      );

      this.pauseReasons.delete(
        'drag'
      );

      this.drag.pressedLink = null;

      if (this.drag.captured) {
        try {
          this.viewport.releasePointerCapture(
            pointerId
          );
        } catch (_) {
          /* 별도 처리 불필요 */
        }
      }

      this.drag.captured = false;
      this.drag.pointerId = null;
      this.scheduleAutoplay();
    }

    openPressedLink(link, event) {
      if (!link) return;

      const href =
        link.getAttribute('href');

      if (!href) return;

      const target =
        link.getAttribute('target');

      const openNewTab =
        target === '_blank' ||
        Boolean(
          event &&
            (event.metaKey ||
              event.ctrlKey)
        );

      /*
        pointerup 직후 브라우저가 별도 click 이벤트를 만들더라도
        같은 링크가 두 번 실행되지 않도록 native click만 잠깐 막습니다.
      */
      this.clickGuardUntil =
        now() + 700;

      if (openNewTab) {
        /*
          noopener/noreferrer를 사용한 window.open()은 브라우저에 따라
          실제 새 탭이 정상적으로 열려도 반환값이 null일 수 있습니다.

          따라서 반환값이 null이라고 현재 탭으로 fallback하면
          "새 탭 + 현재 탭"이 동시에 이동할 수 있으므로 절대 fallback하지 않습니다.
        */
        const opened = window.open(
          href,
          '_blank',
          'noopener,noreferrer'
        );

        if (opened) {
          try {
            opened.opener = null;
          } catch (_) {
            /* 별도 처리 불필요 */
          }
        }

        return;
      }

      window.location.href = href;
    }

    onPointerEnd(event) {
      if (
        !this.drag.active ||
        event.pointerId !==
          this.drag.pointerId
      ) {
        return;
      }

      /* pointerup의 마지막 좌표까지 반영합니다. */
      if (
        Number.isFinite(event.clientX) &&
        Number.isFinite(event.clientY)
      ) {
        this.drag.deltaX =
          event.clientX -
          this.drag.startX;

        this.drag.absX = Math.abs(
          this.drag.deltaX
        );
      }

      const pressedLink =
        this.drag.pressedLink;

      const elapsed = Math.max(
        1,
        now() -
          this.drag.startedAt
      );

      const velocityX =
        this.drag.deltaX /
        elapsed;

      const distanceThreshold =
        Math.max(
          this.settings.dragThreshold,
          Math.min(
            70,
            this.viewportWidth * 0.16
          )
        );

      const movedEnough =
        this.drag.absX >=
        distanceThreshold;

      const flicked =
        Math.abs(velocityX) > 0.6;

      const wasHorizontalDrag =
        this.drag.horizontalLocked &&
        this.drag.absX > 6;

      this.drag.active = false;

      this.root.classList.remove(
        'is-dragging'
      );

      this.track.classList.remove(
        'is-dragging'
      );

      this.pauseReasons.delete(
        'drag'
      );

      const wasCaptured =
        this.drag.captured;

      this.drag.captured = false;
      this.drag.pointerId = null;
      this.drag.pressedLink = null;

      if (wasCaptured) {
        try {
          this.viewport.releasePointerCapture(
            event.pointerId
          );
        } catch (_) {
          /* 별도 처리 불필요 */
        }
      }

      let targetIndex =
        this.index;

      if (
        wasHorizontalDrag &&
        (movedEnough || flicked)
      ) {
        targetIndex +=
          this.drag.deltaX < 0
            ? 1
            : -1;
      }

      if (wasHorizontalDrag) {
        this.clickGuardUntil =
          now() + 320;

        this.goTo(
          targetIndex,
          {
            animate: true,
            announce: true,
            userInitiated: true
          }
        );

        return;
      }

      /*
        가장 중요한 부분:
        단순 클릭/탭은 브라우저 click 이벤트가 생기는지와 관계없이
        pointerup에서 저장해둔 카드 URL로 직접 이동합니다.
      */
      if (pressedLink) {
        this.openPressedLink(
          pressedLink,
          event
        );
        return;
      }

      this.goTo(
        targetIndex,
        {
          animate: true,
          announce: false,
          userInitiated: false
        }
      );

      this.scheduleAutoplay();
    }

    onPointerCancel(event) {
      if (
        !this.drag.active ||
        event.pointerId !==
          this.drag.pointerId
      ) {
        return;
      }

      const pointerId =
        this.drag.pointerId;

      this.drag.active = false;
      this.drag.pressedLink = null;

      this.root.classList.remove(
        'is-dragging'
      );

      this.track.classList.remove(
        'is-dragging'
      );

      this.pauseReasons.delete(
        'drag'
      );

      if (this.drag.captured) {
        try {
          this.viewport.releasePointerCapture(
            pointerId
          );
        } catch (_) {
          /* 별도 처리 불필요 */
        }
      }

      this.drag.captured = false;
      this.drag.pointerId = null;

      this.goTo(this.index, {
        animate: true,
        announce: false,
        userInitiated: false
      });

      this.scheduleAutoplay();
    }

    onLostPointerCapture(event) {
      if (
        !this.drag.active ||
        event.pointerId !==
          this.drag.pointerId
      ) {
        return;
      }

      this.drag.active = false;
      this.drag.captured = false;
      this.drag.pointerId = null;
      this.drag.pressedLink = null;

      this.root.classList.remove(
        'is-dragging'
      );

      this.track.classList.remove(
        'is-dragging'
      );

      this.pauseReasons.delete(
        'drag'
      );

      this.goTo(this.index, {
        animate: true,
        announce: false,
        userInitiated: false
      });

      this.scheduleAutoplay();
    }

    onClickCapture(event) {
      if (
        now() <
        this.clickGuardUntil
      ) {
        event.preventDefault();
        event.stopPropagation();

        if (
          typeof event
            .stopImmediatePropagation ===
          'function'
        ) {
          event.stopImmediatePropagation();
        }
      }
    }

    onMouseEnter() {
      this.pauseReasons.add(
        'hover'
      );

      this.clearAutoplayTimer();
    }

    onMouseLeave() {
      this.pauseReasons.delete(
        'hover'
      );

      this.scheduleAutoplay();
    }

    onFocusIn() {
      this.pauseReasons.add(
        'focus'
      );

      this.clearAutoplayTimer();
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (
          !this.interactionArea.contains(
            document.activeElement
          )
        ) {
          this.pauseReasons.delete(
            'focus'
          );

          this.scheduleAutoplay();
        }
      }, 0);
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pauseReasons.add(
          'hidden'
        );

        this.clearAutoplayTimer();
      } else {
        this.pauseReasons.delete(
          'hidden'
        );

        this.scheduleAutoplay();
      }
    }

    onReducedMotionChange() {
      if (
        this.reducedMotionMedia.matches
      ) {
        this.pauseReasons.add(
          'reduced-motion'
        );

        this.clearAutoplayTimers();
      } else {
        this.pauseReasons.delete(
          'reduced-motion'
        );

        this.scheduleAutoplay();
      }
    }

    handleUserInteraction() {
      this.clearAutoplayTimers();

      if (
        !this.settings.autoplay ||
        this.reducedMotionMedia.matches ||
        this.index >=
          this.maxIndex
      ) {
        return;
      }

      this.autoplayResumeTimer =
        window.setTimeout(() => {
          this.autoplayResumeTimer =
            null;

          this.scheduleAutoplay();
        }, this.settings.resumeDelay);
    }

    canAutoplay() {
      return (
        this.settings.autoplay &&
        !this.reducedMotionMedia
          .matches &&
        !document.hidden &&
        this.maxIndex > 0 &&
        this.index <
          this.maxIndex &&
        this.pauseReasons.size === 0
      );
    }

    scheduleAutoplay() {
      this.clearAutoplayTimer();

      if (!this.canAutoplay()) {
        return;
      }

      this.autoplayTimer =
        window.setTimeout(() => {
          this.autoplayTimer = null;

          if (!this.canAutoplay()) {
            return;
          }

          this.goBy(1, {
            animate: true,
            announce: false,
            userInitiated: false
          });

          this.scheduleAutoplay();
        }, this.settings.autoplayInterval);
    }

    clearAutoplayTimer() {
      if (
        this.autoplayTimer !== null
      ) {
        window.clearTimeout(
          this.autoplayTimer
        );

        this.autoplayTimer = null;
      }
    }

    clearAutoplayTimers() {
      this.clearAutoplayTimer();

      if (
        this.autoplayResumeTimer !==
        null
      ) {
        window.clearTimeout(
          this.autoplayResumeTimer
        );

        this.autoplayResumeTimer =
          null;
      }
    }

    onResize() {
      if (this.destroyed) return;

      if (
        this.resizeFrame !== null
      ) {
        window.cancelAnimationFrame(
          this.resizeFrame
        );
      }

      this.resizeFrame = raf(() => {
        this.resizeFrame = null;

        this.measure(false);
        this.scheduleAutoplay();
      });
    }

    selectBlock(blockId) {
      const targetSlide =
        this.slides.find(
          (slide) =>
            slide.dataset.blockId ===
            blockId
        );

      if (!targetSlide) return;

      const slideIndex =
        this.slides.indexOf(
          targetSlide
        );

      const targetIndex =
        this.mobile
          ? slideIndex
          : clamp(
              slideIndex,
              0,
              this.maxIndex
            );

      this.clearAutoplayTimers();

      this.goTo(targetIndex, {
        animate: true,
        announce: true,
        userInitiated: false
      });
    }

    destroy() {
      this.destroyed = true;

      this.root.classList.remove(
        'is-ready',
        'is-dragging',
        'is-static'
      );

      this.clearAutoplayTimers();

      if (
        this.resizeFrame !== null
      ) {
        window.cancelAnimationFrame(
          this.resizeFrame
        );

        this.resizeFrame = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      this.listeners.forEach(
        ({
          target,
          type,
          handler,
          options
        }) => {
          target.removeEventListener(
            type,
            handler,
            options
          );
        }
      );

      this.listeners = [];

      if (
        typeof this.reducedMotionMedia
          .removeEventListener ===
        'function'
      ) {
        this.reducedMotionMedia.removeEventListener(
          'change',
          this.onReducedMotionChange
        );
      } else if (
        typeof this.reducedMotionMedia
          .removeListener ===
        'function'
      ) {
        this.reducedMotionMedia.removeListener(
          this.onReducedMotionChange
        );
      }

      if (this.track) {
        this.track.style.transform = '';

        this.track.classList.remove(
          'is-dragging',
          'is-no-transition'
        );
      }

      this.slides.forEach((slide) => {
        slide.removeAttribute(
          'aria-hidden'
        );

        slide.removeAttribute(
          'data-current'
        );

        const cardLink =
          slide.querySelector(
            '.bu-content-card-slider__card-link'
          );

        if (cardLink) {
          cardLink.removeAttribute(
            'tabindex'
          );
        }
      });
    }
  }

  const mount = (
    scope = document
  ) => {
    const roots = [];

    if (
      scope.matches &&
      scope.matches(
        ROOT_SELECTOR
      )
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    roots.forEach((root) => {
      if (instances.has(root)) {
        return;
      }

      const instance =
        new BUContentCardSlider(
          root
        );

      instances.set(
        root,
        instance
      );
    });
  };

  const unmount = (
    scope = document
  ) => {
    const roots = [];

    if (
      scope.matches &&
      scope.matches(
        ROOT_SELECTOR
      )
    ) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      roots.push(
        ...scope.querySelectorAll(
          ROOT_SELECTOR
        )
      );
    }

    roots.forEach((root) => {
      const instance =
        instances.get(root);

      if (instance) {
        instance.destroy();
      }

      instances.delete(root);
    });
  };

  let domReadyHandler = null;

  const onSectionLoad = (event) => {
    if (
      event &&
      event.target
    ) {
      mount(event.target);
    }
  };

  const onSectionUnload = (event) => {
    if (
      event &&
      event.target
    ) {
      unmount(event.target);
    }
  };

  const onBlockSelect = (event) => {
    if (
      !event ||
      !event.target
    ) {
      return;
    }

    const slide =
      event.target.closest(
        SLIDE_SELECTOR
      );

    const root =
      event.target.closest(
        ROOT_SELECTOR
      );

    if (!slide || !root) {
      return;
    }

    const instance =
      instances.get(root);

    if (instance) {
      instance.selectBlock(
        slide.dataset.blockId
      );
    }
  };

  const destroyAll = () => {
    instances.forEach((instance) => {
      instance.destroy();
    });

    instances.clear();

    if (domReadyHandler) {
      document.removeEventListener(
        'DOMContentLoaded',
        domReadyHandler
      );
      domReadyHandler = null;
    }

    document.removeEventListener(
      'shopify:section:load',
      onSectionLoad
    );

    document.removeEventListener(
      'shopify:section:unload',
      onSectionUnload
    );

    document.removeEventListener(
      'shopify:block:select',
      onBlockSelect
    );
  };

  document.addEventListener(
    'shopify:section:load',
    onSectionLoad
  );

  document.addEventListener(
    'shopify:section:unload',
    onSectionUnload
  );

  document.addEventListener(
    'shopify:block:select',
    onBlockSelect
  );

  if (
    document.readyState ===
    'loading'
  ) {
    domReadyHandler = () => {
      domReadyHandler = null;
      mount(document);
    };

    document.addEventListener(
      'DOMContentLoaded',
      domReadyHandler,
      { once: true }
    );
  } else {
    mount(document);
  }

  window[REGISTRY_KEY] = {
    version: VERSION,
    mount,
    unmount,
    destroyAll
  };
})();